import React, { useState, useCallback } from 'react';
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Stack,
  Textarea,
  FormControl,
  FormLabel,
  Alert,
  Chip,
  Divider,
  Card,
  CardContent,
  Input,
  Checkbox
} from '@mui/joy';
import {
  PaperPlaneTilt as Send,
  Envelope as Mail,
  XCircle as AlertCircle,
  Clock,
  FileText,
  Users,
  X
} from '@phosphor-icons/react';
import RecipientManager, { isSigningRole, type ContractRecipient } from './RecipientManager';
import type { Contract } from '../../../types';

interface SendContractModalProps {
  open: boolean;
  onClose: () => void;
  contract: Contract;
  onSend: (sendData: SendContractData) => Promise<void>;
  purpose?: 'contract' | 'repair';
}

export interface SendContractData {
  recipients: ContractRecipient[];
  subject: string;
  custom_message: string;
  expires_in_days?: number;
  send_reminders: boolean;
  copy_to_self: boolean;
}

const SendContractModal: React.FC<SendContractModalProps> = ({
  open,
  onClose,
  contract,
  onSend,
  purpose = 'contract'
}) => {
  const [recipients, setRecipients] = useState<ContractRecipient[]>(contract.recipients || []);
  const [subject, setSubject] = useState(`Please sign: ${contract.title}`);
  const [message, setMessage] = useState(purpose === 'repair' ? `Hello,

Please review the scope, price, and terms for your repair using the secure signing link in this email. If everything looks correct, sign to authorize the work.

Please contact us if you have any questions.

Thank you,
${contract.sales_rep_name || 'Your Organization'}` : `Hello,

Please review and sign the attached contract: ${contract.title}

This contract contains ${contract.contract_fields.length} field(s) that require your attention. Please complete all required fields and provide your signature.

Thank you for your time.

Best regards,
${contract.sales_rep_name || 'Your Organization'}`);
  const [expirationDays, setExpirationDays] = useState<number>(30);
  const [sendReminders, setSendReminders] = useState(true);
  const [sendCopyToSelf, setSendCopyToSelf] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signers = recipients.filter(r => isSigningRole(r.role));
  const hasRequiredFields = contract.contract_fields.some(f => f.required);
  const canSend = recipients.length > 0 && signers.length > 0 && subject.trim() && message.trim();

  const handleSend = useCallback(async () => {
    if (!canSend) return;

    setSending(true);
    setError(null);

    try {
      const sendData: SendContractData = {
        recipients,
        subject: subject.trim(),
        custom_message: message.trim(),
        expires_in_days: expirationDays > 0 ? expirationDays : undefined,
        send_reminders: sendReminders,
        copy_to_self: sendCopyToSelf
      };

      await onSend(sendData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send contract');
    } finally {
      setSending(false);
    }
  }, [canSend, recipients, subject, message, expirationDays, sendReminders, sendCopyToSelf, onSend, onClose]);

  const handleClose = useCallback(() => {
    if (!sending) {
      onClose();
    }
  }, [sending, onClose]);

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog size="lg" sx={{ width: 'min(800px, calc(100vw - 32px))', maxHeight: '90vh', overflow: 'auto', p: { xs: 2, sm: 3 } }}>
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <Send size={20} />
            {purpose === 'repair' ? 'Send repair proposal' : 'Send Contract for Signing'}
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3}>
            {/* Contract Summary */}
            <Card variant="soft" color="primary" size="sm">
              <CardContent>
                <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                  <FileText size={24} />
                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <Typography level="title-sm" fontWeight="lg">
                      {contract.title}
                    </Typography>
                    <Typography level="body-xs" textColor="text.secondary">
                      Contract #{contract.contract_number} • {contract.customer_name}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Chip size="sm" startDecorator={<FileText size={12} />}>
                      {contract.contract_fields.length} field(s)
                    </Chip>
                    <Chip size="sm" startDecorator={<Users size={12} />}>
                      {signers.length} signer(s)
                    </Chip>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* Recipients Management */}
            <RecipientManager
              contract={contract}
              onRecipientsChange={setRecipients}
            />

            <Divider />

            {/* Email Configuration */}
            <Box>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Email Settings
              </Typography>

              <Stack spacing={2}>
                <FormControl>
                  <FormLabel>Subject Line</FormLabel>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Please sign: Contract Title"
                    startDecorator={<Mail size={16} />}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Email Message</FormLabel>
                  <Textarea
                    minRows={6}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Enter your message to recipients..."
                  />
                  <Typography level="body-xs" textColor="text.secondary" sx={{ mt: 0.5 }}>
                    This message will be included in each recipient’s signing invitation.
                  </Typography>
                </FormControl>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Expiration (days)</FormLabel>
                    <Input
                      type="number"
                      value={expirationDays}
                      onChange={(e) => setExpirationDays(parseInt(e.target.value) || 0)}
                      endDecorator="days"
                      startDecorator={<Clock size={16} />}
                      slotProps={{
                        input: {
                          min: 1,
                          max: 365
                        }
                      }}
                    />
                  </FormControl>

                  {/* Two independent checkboxes: a shared FormControl would give both
                      the same generated id and mislabel them for screen readers. */}
                  <Box sx={{ flex: 1 }}>
                    <FormLabel component="legend">Follow-up</FormLabel>
                    <Stack spacing={1} sx={{ pt: 1 }}>
                      <Checkbox
                        label="Send automatic reminders to unsigned recipients"
                        checked={sendReminders}
                        onChange={(e) => setSendReminders(e.target.checked)}
                      />
                      <Checkbox
                        label="Send me a copy of this contract"
                        checked={sendCopyToSelf}
                        onChange={(e) => setSendCopyToSelf(e.target.checked)}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Stack>
            </Box>

            {/* Validation Warnings */}
            {!hasRequiredFields && (
              <Alert color="warning">
                <AlertCircle size={16} />
                <Typography level="body-sm">
                  This contract has no required fields. Recipients will be able to submit without filling any fields.
                </Typography>
              </Alert>
            )}

            {signers.length === 0 && recipients.length > 0 && (
              <Alert color="warning">
                <AlertCircle size={16} />
                <Typography level="body-sm">
                  You have recipients added but no signers. At least one recipient must be a signer.
                </Typography>
              </Alert>
            )}

            {error && (
              <Alert color="danger">
                <AlertCircle size={16} />
                {error}
              </Alert>
            )}

            {/* Sending Summary */}
            {canSend && (
              <Card variant="soft" color="success" size="sm">
                <CardContent>
                  <Typography level="body-sm">
                    <strong>Ready to send!</strong> {signers.length} signer(s) and {recipients.filter(r => !isSigningRole(r.role)).length} other recipient(s) will receive a secure link by email.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={sending}
            startDecorator={<X size={16} />}
          >
            Cancel
          </Button>
          
          <Button
            variant="solid"
            color="primary"
            onClick={handleSend}
            loading={sending}
            disabled={!canSend}
            startDecorator={<Send size={16} />}
          >
            {sending ? 'Sending...' : `Send to ${recipients.length} recipient(s)`}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default SendContractModal;
export { SendContractModal };
